import { exportMarkets } from "@/mocks/brands";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
    { "@type": "ListItem", "position": 2, "name": "Markets", "item": siteUrl + "/markets" }
  ]
};

const countryPositions: Record<string, { top: string; left: string }> = {
  "South Korea": { top: "44%", left: "80%" },
  Mexico: { top: "48%", left: "15%" },
  "United States": { top: "32%", left: "16%" },
  Canada: { top: "20%", left: "14%" },
  "United Arab Emirates": { top: "46%", left: "58%" },
  "Saudi Arabia": { top: "42%", left: "57%" },
  Lithuania: { top: "22%", left: "50%" },
  Singapore: { top: "55%", left: "72%" },
  Mongolia: { top: "30%", left: "72%" },
  Kazakhstan: { top: "32%", left: "64%" },
};

export default function MarketsPage() {
  usePageSEO({
    title: "Global K-Beauty Export Markets | GH Corporation",
  });

  return (
    <section className="relative w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-secondary-100 overflow-hidden">
      <JsonLd data={breadcrumbSchema} />
      <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-accent-100/60 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
                Global Markets
              </span>
            </div>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light">
              Supporting K-Beauty Buyers
              <br />
              <span className="italic">Across Global Markets</span>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-foreground-700 text-[15px] md:text-base leading-relaxed font-light">
              GH Corporation supports sourcing, packing and freight planning for buyers across North America, Latin America, Europe, the Middle East, Southeast Asia and Central Asia. Market status and service scope may vary by destination, product and local import requirements.
            </p>
          </div>
        </div>

        <div className="mt-14 relative">
          <div className="relative w-full rounded-2xl overflow-hidden border border-background-200/80 bg-background-50">
            <div className="relative w-full" style={{ paddingBottom: "52%" }}>
              <img
                src="https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/554bc589-f5e9-4672-bf7a-0a939dcd4c15_compressed_ChatGPT-Image-2026-9-16--02_03_31.webp"
                alt="Global Export Markets Map — Korea to worldwide destinations"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background-50/30 via-transparent to-background-50/30 pointer-events-none" />
            </div>

            {/* Country labels only — dots removed */}
            {exportMarkets.map((m) => {
              const pos = countryPositions[m.country];
              if (!pos) return null;
              return (
                <div
                  key={m.country}
                  className="absolute group z-10"
                  style={{ top: pos.top, left: pos.left }}
                >
                  <div className="relative">
                    <span className="font-label text-[11px] tracking-[0.18em] uppercase text-foreground-950 bg-background-50/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {m.country}
                    </span>
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                    <div className="bg-foreground-950 text-background-50 text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                      <div className="font-heading text-xs tracking-normal normal-case">{m.country}</div>
                      <div className="text-background-50/60 text-[9px] mt-0.5">{m.region}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* South Korea label only — dot removed */}
            <div
              className="absolute z-10"
              style={{ top: countryPositions["South Korea"].top, left: countryPositions["South Korea"].left }}
            >
              <span className="font-label text-[11px] tracking-[0.18em] uppercase text-foreground-950 bg-background-50/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm whitespace-nowrap">
                South Korea
              </span>
            </div>
          </div>

          {/* Export Experience */}
          <div className="mt-10">
            <div className="flex items-center gap-3 mb-4 justify-center">
              <span className="w-2 h-2 rounded-full bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.18em] uppercase text-foreground-800">
                Export Experience — Current and Target Markets
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {exportMarkets.map((m, i) => (
              <div
                key={m.country}
                className="flex items-center gap-2 text-foreground-700 group cursor-pointer"
              >
                <span className={`w-2 h-2 rounded-full ${i < 6 ? "bg-accent-500" : "bg-foreground-400"}`} />
                <span className="font-label text-[11px] tracking-[0.14em] uppercase group-hover:text-foreground-950 transition-colors">
                  {m.country}
                </span>
                <span className="text-foreground-400 text-[10px] tracking-[0.1em] uppercase hidden sm:inline">
                  {m.region}
                </span>
              </div>
            ))}
            </div>
            <div className="mt-5 flex items-center justify-center gap-6 text-[12px]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent-500" />
                <span className="text-foreground-600 font-label tracking-[0.1em] uppercase">Export Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-foreground-400" />
                <span className="text-foreground-600 font-label tracking-[0.1em] uppercase">Target Market / Serviceable Region</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6 border-t border-background-300/60 pt-12">
          {[
            { num: "125+", label: "Korean Beauty Brands" },
            { num: "9", label: "Current & Target Markets", sup: "destinations" },
            { num: "FCL · LCL · AIR", label: "Freight Options" },
            { num: "Mixed Order", label: "Multi-Brand Sourcing" },
          ].map((s) => (
            <div key={s.label}>
              <div className="font-heading text-foreground-950 text-5xl md:text-6xl leading-none font-light flex items-start gap-1">
                <span>{s.num}</span>
                {s.sup && (
                  <span className="font-label text-[11px] tracking-[0.2em] uppercase text-foreground-500 mt-2">
                    {s.sup}
                  </span>
                )}
              </div>
              <div className="mt-4 font-label text-[11px] tracking-[0.2em] uppercase text-foreground-600">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}